
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const WeBuyFireDamagedPropertyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="We Buy Fire Damaged Property Fast El Paso TX | Cash for Burned Houses | TREX RE LLC"
        description="Sell your fire damaged house fast in El Paso, TX. We buy burned homes as-is for cash - no repairs, no cleanup, no inspections. Get a fair cash offer within 24 hours and close in as little as 7 days."
        keywords="sell fire damaged house El Paso, buy burned property Texas, cash for fire damaged home, sell house after fire El Paso TX, fire damage property buyers"
        canonical="/elpaso-tx/we-buy-fire-damaged-property"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "We Buy Fire Damaged Property Fast in El Paso, TX",
          "description": "Complete guide to selling fire damaged property in El Paso. Learn how cash buyers purchase burned homes as-is, the process, timeline, and how to get a fair offer.",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          }
        }}
      />
      <GlobalNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ff7d9a860b91d9e54c7e5f27749e94ba.jpg"
            alt="Fire damaged property in El Paso Texas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-full mb-6">
              Fire Damage Property Buyers
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              We Buy Fire Damaged Property <span className="text-orange-400">Fast</span> in El Paso
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Dealing with a fire-damaged home is overwhelming. We make it simple by purchasing your property as-is — no repairs, no cleanup, no waiting. Get a fair cash offer within 24 hours and close on your timeline.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/get-cash-offer-for-my-property"
                className="inline-flex items-center px-8 py-4 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-fire-line mr-2"></i>
                Get Your Cash Offer
              </a>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (915) 206-5814
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              A house fire is one of the most devastating events a homeowner can experience. Beyond the emotional trauma of losing personal belongings and memories, you're suddenly faced with complex decisions about insurance claims, repairs, and whether to rebuild or sell. For many El Paso homeowners, the prospect of restoring a fire-damaged property feels insurmountable — and that's where we come in.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              At TREX RE LLC, we specialize in purchasing fire-damaged properties throughout El Paso and the surrounding areas. We understand that after a fire, you need solutions, not more problems. That's why we buy burned homes in any condition, handle all the complexities, and provide you with a fair cash offer that lets you move forward with your life.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Whether your property suffered minor smoke damage or was completely gutted by flames, we're ready to make you an offer. There's no need to clean up, make repairs, or even deal with contractors. We buy the property exactly as it stands today.
            </p>
          </div>
        </div>
      </section>

      {/* Understanding Fire Damage Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Understanding Fire Damage to <span className="text-orange-500">Your Property</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fire damage extends far beyond what's visible to the naked eye
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=interior%20of%20fire%20damaged%20home%20showing%20structural%20damage%20burned%20walls%20charred%20ceiling%20beams%20smoke%20stains%20professional%20property%20damage%20assessment%20photography%20showing%20extent%20of%20fire%20destruction&width=700&height=500&seq=fire-damage-interior-002&orientation=landscape"
                alt="Interior fire damage showing structural issues"
                className="w-full h-[400px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                When a fire tears through a home, the destruction goes far deeper than the charred walls and melted fixtures you can see. Understanding the full scope of fire damage helps explain why traditional home sales become nearly impossible — and why selling to a cash buyer often makes the most sense.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-fire-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Structural Damage</h4>
                    <p className="text-gray-600">Fire weakens load-bearing walls, roof trusses, floor joists, and foundation elements. Even areas that appear intact may have compromised structural integrity.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-cloud-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Smoke and Soot Penetration</h4>
                    <p className="text-gray-600">Smoke infiltrates every crack and crevice, embedding itself in insulation, ductwork, and porous materials. The smell can persist for years without professional remediation.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-drop-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Water Damage</h4>
                    <p className="text-gray-600">Firefighting efforts often cause extensive water damage, leading to mold growth, warped materials, and electrical hazards that compound the fire damage.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-flashlight-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Electrical and Plumbing Systems</h4>
                    <p className="text-gray-600">Heat damages wiring insulation, melts pipes, and destroys HVAC systems. Complete replacement is often necessary even in partially damaged homes.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Selling Fire Damaged Property is Difficult */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Selling Fire Damaged Property is <span className="text-orange-500">So Difficult</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Traditional real estate channels present significant obstacles for fire-damaged homes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-lg mb-6">
                <i className="ri-bank-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Financing Restrictions</h3>
              <p className="text-gray-600 leading-relaxed">
                Most lenders won't approve mortgages for fire-damaged properties. This eliminates the vast majority of potential buyers who need financing, leaving only cash buyers as viable options.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-lg mb-6">
                <i className="ri-search-eye-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Inspection Failures</h3>
              <p className="text-gray-600 leading-relaxed">
                Fire-damaged homes fail standard inspections, triggering buyer contingencies and deal cancellations. Even minor fire damage raises red flags that scare away traditional buyers.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-lg mb-6">
                <i className="ri-shield-cross-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Insurance Complications</h3>
              <p className="text-gray-600 leading-relaxed">
                Buyers can't obtain homeowner's insurance on fire-damaged properties until repairs are complete. Without insurance, no lender will approve a loan, creating a catch-22 situation.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-lg mb-6">
                <i className="ri-money-dollar-circle-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Repair Cost Uncertainty</h3>
              <p className="text-gray-600 leading-relaxed">
                Hidden damage often reveals itself during repairs, causing costs to spiral. Buyers fear the unknown, and contractors are hesitant to provide fixed-price quotes for fire restoration.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-lg mb-6">
                <i className="ri-user-unfollow-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Agent Reluctance</h3>
              <p className="text-gray-600 leading-relaxed">
                Many real estate agents refuse to list fire-damaged properties because they're difficult to sell and require specialized marketing. Those who do often demand higher commissions.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-lg mb-6">
                <i className="ri-time-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Extended Timeline</h3>
              <p className="text-gray-600 leading-relaxed">
                Fire-damaged homes sit on the market for months or years. Meanwhile, you're paying property taxes, insurance, and potentially mortgage payments on a property you can't use.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-orange-50 rounded-xl p-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              These challenges explain why many homeowners who <a href="/elpaso-tx/cant-afford-repairs-to-sell-house" className="text-orange-600 hover:text-orange-700 font-semibold">can't afford the repairs needed to sell their house</a> turn to cash buyers. When your property has suffered fire damage, the traditional real estate market simply isn't designed to help you. You need a buyer who specializes in distressed properties and has the resources to handle the restoration themselves.
            </p>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How We Buy Fire Damaged <span className="text-orange-500">Properties</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined process gets you from fire damage to cash in hand quickly
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="order-2 lg:order-1">
              <div className="space-y-8">
                <div className="flex gap-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-500 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Contact Us About Your Property</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Call us or fill out our online form with basic information about your fire-damaged property. We'll ask about the extent of the damage, your timeline, and your goals. There's no obligation and no pressure.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-500 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">We Assess the Property</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our team will visit your property to evaluate the fire damage firsthand. We look at structural integrity, salvageable elements, and restoration potential. This assessment is free and helps us make a fair offer.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-500 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Receive Your Cash Offer</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Within 24 hours of our assessment, you'll receive a fair, no-obligation cash offer. We explain exactly how we arrived at our number and answer any questions you have. Take your time to consider it.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-16 h-16 flex items-center justify-center bg-orange-500 text-white rounded-full flex-shrink-0 text-2xl font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Close on Your Timeline</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Accept our offer and choose your closing date. We can close in as little as 7 days or wait longer if you need time. We handle all paperwork and pay all closing costs. You walk away with cash.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=real%20estate%20professional%20meeting%20with%20homeowner%20reviewing%20property%20documents%20at%20kitchen%20table%20discussing%20cash%20offer%20for%20damaged%20home%20warm%20natural%20lighting%20professional%20business%20photography%20showing%20consultation&width=700&height=600&seq=fire-damage-process-003&orientation=landscape"
                alt="Property assessment and cash offer consultation"
                className="w-full h-[500px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Types of Fire Damage We Buy */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Types of Fire Damage <span className="text-orange-500">We Purchase</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From minor smoke damage to total loss — we buy properties in any condition
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-8 border border-orange-100">
              <img
                src="https://readdy.ai/api/search-image?query=house%20with%20minor%20smoke%20damage%20showing%20discoloration%20on%20walls%20and%20ceiling%20soot%20stains%20around%20windows%20southwestern%20style%20home%20interior%20professional%20property%20damage%20photography&width=600&height=350&seq=fire-damage-minor-004&orientation=landscape"
                alt="Minor smoke and soot damage"
                className="w-full h-[250px] object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Minor Smoke and Soot Damage</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Even "minor" fire damage can make a home difficult to sell traditionally. Smoke odors permeate fabrics, carpets, and porous surfaces. Soot stains walls and ceilings. While the structure may be sound, the cleanup costs and lingering smell deter most buyers.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Smoke-stained walls and ceilings
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Soot deposits throughout the home
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Persistent smoke odors
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Damaged HVAC systems from smoke infiltration
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-8 border border-orange-100">
              <img
                src="https://readdy.ai/api/search-image?query=house%20with%20moderate%20fire%20damage%20showing%20burned%20kitchen%20area%20charred%20cabinets%20melted%20appliances%20but%20intact%20structure%20southwestern%20home%20professional%20property%20damage%20assessment%20photography&width=600&height=350&seq=fire-damage-moderate-005&orientation=landscape"
                alt="Moderate structural fire damage"
                className="w-full h-[250px] object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Moderate Structural Damage</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                When fire damages one or more rooms significantly, restoration becomes a major undertaking. Walls may need to be torn out, electrical systems replaced, and structural elements reinforced. These properties require substantial investment to restore.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Kitchen or bathroom fires
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Electrical fire damage
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Partial roof or attic damage
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Water damage from firefighting efforts
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-8 border border-orange-100">
              <img
                src="https://readdy.ai/api/search-image?query=severely%20fire%20damaged%20house%20exterior%20showing%20extensive%20burn%20damage%20collapsed%20roof%20charred%20walls%20total%20loss%20property%20el%20paso%20texas%20desert%20landscape%20professional%20real%20estate%20photography&width=600&height=350&seq=fire-damage-severe-006&orientation=landscape"
                alt="Severe fire damage total loss"
                className="w-full h-[250px] object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Severe Damage and Total Loss</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Even when a fire has destroyed most of the structure, the land retains value. We purchase properties where the home is a complete loss, handling demolition and site preparation ourselves. You don't need to deal with any of it.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Complete structural failure
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Properties condemned by the city
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Homes requiring demolition
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Land value purchases
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-8 border border-orange-100">
              <img
                src="https://readdy.ai/api/search-image?query=house%20showing%20combination%20of%20fire%20and%20water%20damage%20mold%20growth%20on%20walls%20water%20stains%20ceiling%20damage%20from%20firefighting%20efforts%20professional%20property%20damage%20documentation%20photography&width=600&height=350&seq=fire-damage-combo-007&orientation=landscape"
                alt="Combined fire and water damage"
                className="w-full h-[250px] object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Combined Fire and Water Damage</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Firefighting efforts often cause as much damage as the fire itself. Water saturates walls, floors, and foundations. If not addressed quickly, mold develops within 24-48 hours. We buy properties with this compound damage.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Mold growth from water damage
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Warped floors and walls
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Foundation water intrusion
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-orange-500"></i>
                  Rusted electrical and plumbing
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              No matter what type of fire damage your property has sustained, we're interested in making you an offer. We've purchased homes with every level of damage imaginable, and we have the experience and resources to handle even the most challenging situations. If you're dealing with <a href="/sell-distressed-property" className="text-orange-600 hover:text-orange-700 font-semibold">distressed property that needs to be sold quickly</a>, we can help.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Benefits of Selling Your Fire Damaged <span className="text-orange-400">Home to Us</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Skip the stress and uncertainty — get cash for your property today
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-800 rounded-xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-500 rounded-full mx-auto mb-6">
                <i className="ri-tools-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">No Repairs Required</h3>
              <p className="text-gray-400">
                We buy your property exactly as it stands. No cleanup, no restoration, no contractor headaches. Leave everything as-is.
              </p>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-500 rounded-full mx-auto mb-6">
                <i className="ri-speed-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Fast Closing</h3>
              <p className="text-gray-400">
                Close in as little as 7 days. No waiting for buyer financing, inspections, or appraisals. Get your cash quickly.
              </p>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-500 rounded-full mx-auto mb-6">
                <i className="ri-money-dollar-box-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">All Cash Offer</h3>
              <p className="text-gray-400">
                No financing contingencies or loan approvals to worry about. We pay cash, so the deal won't fall through.
              </p>
            </div>

            <div className="bg-gray-800 rounded-xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-500 rounded-full mx-auto mb-6">
                <i className="ri-hand-coin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">No Fees or Commissions</h3>
              <p className="text-gray-400">
                We pay all closing costs. No real estate agent commissions, no hidden fees. The offer we make is what you receive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Claims Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=homeowner%20reviewing%20insurance%20claim%20documents%20with%20adjuster%20at%20fire%20damaged%20property%20professional%20business%20meeting%20discussing%20settlement%20paperwork%20warm%20natural%20lighting&width=700&height=500&seq=fire-damage-insurance-008&orientation=landscape"
                alt="Insurance claim review process"
                className="w-full h-[450px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Navigating Insurance Claims <span className="text-orange-500">After a Fire</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Dealing with insurance companies after a fire can be frustrating and time-consuming. Claims are often disputed, settlements delayed, and payouts may not cover the full cost of restoration. Many homeowners find themselves caught between an insurance company that won't pay enough and a property they can't afford to fix.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We can work with you regardless of your insurance situation. Whether you've already received a settlement, are still negotiating with your insurer, or your claim was denied, we can make you a cash offer for your property. In some cases, selling to us and using the proceeds along with your insurance settlement gives you the fresh start you need.
              </p>
              <div className="bg-orange-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-3">Common Insurance Challenges We Help With:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-orange-500"></i>
                    Settlements that don't cover full restoration costs
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-orange-500"></i>
                    Disputed claims or denied coverage
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-orange-500"></i>
                    Underinsured properties
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-orange-500"></i>
                    Lengthy claim processing delays
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-orange-500"></i>
                    Properties with lapsed insurance
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Situations Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Common Situations Where Selling <span className="text-orange-500">Makes Sense</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every fire damage situation is unique — here's when selling to a cash buyer is often the best choice
            </p>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                  <i className="ri-home-heart-line text-orange-500 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You've Inherited a Fire-Damaged Property</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Inheriting a property that's been damaged by fire creates a complex situation. You may be dealing with probate, multiple heirs, and a property that needs significant work. Many families who <a href="/inherited-homes" className="text-orange-600 hover:text-orange-700 font-semibold">inherit homes with complications</a> find that selling for cash is the simplest path forward. We can work with estates, handle title issues, and coordinate with all parties to make the sale as smooth as possible.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                  <i className="ri-bank-card-line text-orange-500 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You're Facing Financial Hardship</h3>
                  <p className="text-gray-600 leading-relaxed">
                    A fire can devastate your finances. Between temporary housing costs, replacing belongings, and continuing to pay your mortgage on a home you can't live in, the financial pressure mounts quickly. If you're <a href="/avoid-foreclosure" className="text-orange-600 hover:text-orange-700 font-semibold">struggling to avoid foreclosure</a> or simply need to stop the financial bleeding, selling your fire-damaged property for cash can provide immediate relief and a fresh start.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-orange-500 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Need to Relocate Quickly</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Sometimes life doesn't wait for you to deal with a fire-damaged property. Job transfers, family emergencies, or military PCS orders may require you to move quickly. If you're a service member at Fort Bliss who needs to <a href="/elpaso-tx/selling-home-during-pcs" className="text-orange-600 hover:text-orange-700 font-semibold">sell during a PCS move</a>, we understand the time constraints you're facing and can close on your schedule.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                  <i className="ri-building-2-line text-orange-500 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">The Property Was a Rental Investment</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Landlords face unique challenges when rental properties suffer fire damage. You may have tenants who need to be relocated, insurance complications, and lost rental income. If you're tired of dealing with <a href="/rental-property" className="text-orange-600 hover:text-orange-700 font-semibold">rental property headaches</a>, selling your fire-damaged investment property for cash lets you exit cleanly and redeploy your capital elsewhere.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                  <i className="ri-emotion-sad-line text-orange-500 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Simply Want to Move On</h3>
                  <p className="text-gray-600 leading-relaxed">
                    After a fire, many homeowners simply don't want to return to the property. The memories of the fire, the stress of restoration, and the uncertainty of the process can be overwhelming. There's no shame in wanting a clean break. Selling to us allows you to close this chapter and focus on building your future somewhere new.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What to Expect When <span className="text-orange-500">You Contact Us</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparency and honesty are at the core of how we do business
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                We know that selling a fire-damaged property can feel overwhelming, especially when you're already dealing with the aftermath of a traumatic event. That's why we've designed our process to be as straightforward and stress-free as possible.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                When you reach out to us, you'll speak with a real person — not a call center or automated system. We'll listen to your situation, answer your questions honestly, and explain exactly how we can help. There's never any pressure to accept our offer, and we'll give you all the time you need to make the right decision for your family.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our offers are based on a thorough assessment of your property's condition, location, and the local market. We'll explain our valuation process so you understand exactly how we arrived at our number. Unlike some buyers who make lowball offers hoping you're desperate, we pride ourselves on making fair offers that reflect the true value of your property.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                If you're also exploring other options, that's completely fine. Some homeowners prefer to work with <a href="/elpaso-tx/cash-home-buyers-el-paso" className="text-orange-600 hover:text-orange-700 font-semibold">cash home buyers in El Paso</a> who specialize in different types of properties. We encourage you to get multiple offers and choose the one that's best for your situation.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=friendly%20real%20estate%20professional%20having%20honest%20conversation%20with%20homeowner%20in%20living%20room%20discussing%20property%20sale%20options%20warm%20natural%20lighting%20showing%20trust%20and%20transparency%20professional%20photography&width=700&height=550&seq=fire-damage-expect-009&orientation=landscape"
                alt="Honest conversation about property options"
                className="w-full h-[450px] object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-orange-500">Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about selling fire-damaged property
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">How do you determine the value of a fire-damaged property?</h3>
              <p className="text-gray-600 leading-relaxed">
                We consider several factors: the land value, the extent of the damage, restoration costs, the local real estate market, and comparable sales in the area. We also factor in the property's potential after restoration. Our goal is to make a fair offer that works for both parties.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Do I need to clean up or secure the property before selling?</h3>
              <p className="text-gray-600 leading-relaxed">
                No. We buy properties exactly as they are. You don't need to clean up debris, board windows, or make any repairs. If you've already started cleanup, that's fine too — we'll work with the property in whatever state it's in.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What if I still owe money on my mortgage?</h3>
              <p className="text-gray-600 leading-relaxed">
                We can still purchase your property. At closing, the proceeds from the sale will first pay off your existing mortgage, and you'll receive the remaining balance. If you owe more than the property is worth, we can discuss options including working with your lender on a short sale.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">How quickly can you close on a fire-damaged property?</h3>
              <p className="text-gray-600 leading-relaxed">
                We can close in as little as 7 days if you need to move quickly. However, we're flexible and can work with your timeline. If you need more time to find a new place to live or coordinate with insurance, we can schedule closing for whenever works best for you.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Will I receive less than market value for my property?</h3>
              <p className="text-gray-600 leading-relaxed">
                Our offers reflect the property's current condition, not what it would be worth fully restored. While you may receive less than a fully renovated home would sell for, you're also avoiding tens of thousands in repair costs, months of waiting, and the uncertainty of traditional sales. For many sellers, the speed, certainty, and convenience make our offers the better overall value.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What if there are title issues or liens on the property?</h3>
              <p className="text-gray-600 leading-relaxed">
                We have experience working with properties that have <a href="/title-issues" className="text-orange-600 hover:text-orange-700 font-semibold">title complications</a>. Whether there are liens, judgments, or unclear ownership, we can often find solutions. Our team will work with title companies and attorneys to resolve issues and complete the sale.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Can I sell if the fire is still under investigation?</h3>
              <p className="text-gray-600 leading-relaxed">
                In most cases, yes. However, if the property is part of an active criminal investigation, there may be restrictions. We recommend consulting with your insurance company and any investigators involved. Once the property is released, we can move forward with the purchase.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Do you buy fire-damaged commercial properties too?</h3>
              <p className="text-gray-600 leading-relaxed">
                Yes, we purchase fire-damaged properties of all types, including commercial buildings, multi-family units, and vacant land with fire-damaged structures. Contact us to discuss your specific situation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Sell Your Fire Damaged Property?
          </h2>
          <p className="text-xl text-orange-100 mb-8 leading-relaxed">
            Don't let a fire-damaged property hold you back. Get a fair cash offer today and move forward with your life. No repairs, no cleanup, no waiting — just a simple, straightforward sale.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/get-cash-offer-for-my-property"
              className="inline-flex items-center px-8 py-4 bg-white text-orange-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-line mr-2"></i>
              Get Your Free Cash Offer
            </a>
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-8 py-4 bg-orange-700 text-white font-semibold rounded-lg hover:bg-orange-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814
            </a>
          </div>
          <p className="text-orange-200 mt-8 text-sm">
            Serving El Paso, TX and surrounding areas. Available 7 days a week.
          </p>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Related Resources</h2>
            <p className="text-gray-600">Explore more helpful guides for El Paso property owners</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <a href="/elpaso-tx/investors-buy-house-as-is" className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=real%20estate%20investor%20meeting%20with%20homeowner%20discussing%20as%20is%20property%20purchase%20professional%20business%20setting%20warm%20lighting&width=400&height=250&seq=fire-related-001&orientation=landscape"
                  alt="Investors buy houses as-is"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">Investors That Buy Houses As-Is</h3>
                <p className="text-gray-600 text-sm">Learn how real estate investors purchase properties in any condition.</p>
              </div>
            </a>
            <a href="/sell-distressed-property" className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=distressed%20property%20exterior%20showing%20deferred%20maintenance%20southwestern%20style%20home%20el%20paso%20texas%20professional%20real%20estate%20photography&width=400&height=250&seq=fire-related-002&orientation=landscape"
                  alt="Sell distressed property"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">Sell Distressed Property</h3>
                <p className="text-gray-600 text-sm">Options for selling properties with various types of damage or issues.</p>
              </div>
            </a>
            <a href="/elpaso-tx/sell-house-fast" className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=sold%20sign%20in%20front%20of%20southwestern%20home%20el%20paso%20texas%20fast%20home%20sale%20success%20professional%20real%20estate%20photography&width=400&height=250&seq=fire-related-003&orientation=landscape"
                  alt="Sell house fast El Paso"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">Sell Your House Fast</h3>
                <p className="text-gray-600 text-sm">Complete guide to selling your El Paso home quickly for cash.</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WeBuyFireDamagedPropertyPage;
